import { EnabledAction, ScopeOption } from "../../../../../../models/CommonModel";
import { APICategory } from "../../../../../../models/Entities";
import { getDependentModels } from "../../ScopeConfirmationDialog";
import { ScopeEdit } from "../types";

export interface DependentScopeGroup {
  /** The scope the source edit set; drives the confirm copy (turned off vs on) and the checkbox gate. */
  scope: ScopeOption;
  /** Dependent model names the backend will cascade, raw as the API returns them (formatted for display at render). */
  models: string[];
}

/**
 * For each pending OBJECT-level READ edit, asks the backend which other models the change cascades
 * to, returning a group per edit that actually has dependents. WRITE edits, field edits, and edits
 * with no dependents are skipped (the same gate ScopeConfirmationDialog applies). The backend
 * applies the cascade itself at save time, so these groups exist only to warn the user first; the
 * caller does NOT add the dependents to the edit set.
 */
export const fetchDependentScopeGroups = async (
  edits: ScopeEdit[],
  category: APICategory,
): Promise<DependentScopeGroup[]> => {
  const objectReadEdits = edits.filter(
    (edit): edit is Extract<ScopeEdit, { kind: "model" }> =>
      edit.kind === "model" && edit.capability === EnabledAction.READ,
  );

  const groups = await Promise.all(
    objectReadEdits.map(async (edit) => {
      const result = await getDependentModels(edit.modelName, edit.scope, category);
      const models = result?.body?.dependent_models ?? [];
      if (models.length === 0) return null;
      return { scope: edit.scope, models };
    }),
  );

  return groups.filter((group): group is DependentScopeGroup => group !== null);
};
